import {
	type Jast,
	OPERATOR_EQUALS,
	OPERATOR_LIKE,
	creators,
	print,
	COMPOUND_OPERATOR_OR,
	COMPOUND_OPERATOR_AND,
} from '@atlaskit/jql-ast';
import {
	TextSearchInputValueCollectingVisitor,
	isIssueKeyValue,
} from '@atlassian/jira-jql-utils/src/utils/text-search-input-clause/text-search-input-value-collecting-visitor.tsx';
import { getTextSearchInputClause } from '@atlassian/jira-jql-utils/src/utils/text-search-input-clause/use-text-search-input-clause.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ISSUE_KEY, getTextSearchInputField } from '../../../common/constants.tsx';
import type { TextSearchInputClauseType } from '../../../common/types.tsx';
import { escapeBackslashes, wrapWithQuotes } from '../../../common/utils.tsx';

export type TextValueType = string | undefined;

/**
 * Check if search input clause is valid
 */
const validate = (compoundClause: TextSearchInputClauseType): boolean => {
	if (!compoundClause) {
		return true;
	}

	try {
		compoundClause.accept(new TextSearchInputValueCollectingVisitor(getTextSearchInputField()));

		return true;
	} catch (e) {
		return false;
	}
};

/**
 * Return value of search input clause or undefined if it's invalid or empty
 */
const value = (textSearchClause: TextSearchInputClauseType): TextValueType => {
	if (!textSearchClause) {
		return undefined;
	}

	try {
		return textSearchClause.accept(
			new TextSearchInputValueCollectingVisitor(getTextSearchInputField()),
		);
	} catch (e) {
		return undefined;
	}
};

const createTextSearchInputClause = ({
	quotedKeyword,
	isIssueKey,
}: {
	quotedKeyword: string;
	isIssueKey: boolean;
}): NonNullable<TextSearchInputClauseType> => {
	const textSearchInputClause = creators.terminalClause(
		creators.field(getTextSearchInputField()),
		creators.operator(OPERATOR_LIKE),
		creators.byText.valueOperand(quotedKeyword),
	);

	if (isIssueKey) {
		const issueKeyClause = creators.terminalClause(
			creators.field(ISSUE_KEY),
			creators.operator(OPERATOR_EQUALS),
			creators.byText.valueOperand(quotedKeyword),
		);

		return creators.compoundClause(creators.compoundOperator(COMPOUND_OPERATOR_OR), [
			textSearchInputClause,
			issueKeyClause,
		]);
	}

	return textSearchInputClause;
};

/**
 * Update search input clause with new value
 */
const update = (
	ast: Jast,
	textSearchInputClauseOld: TextSearchInputClauseType,
	val: TextValueType,
): string => {
	const textSearchInputClause = fg('too_many_text_search_input_clauses_fix')
		? getTextSearchInputClause(ast, getTextSearchInputField())
		: textSearchInputClauseOld;

	if (!validate(textSearchInputClause)) {
		return ast.represents;
	}

	if (val === undefined || val === null || val === '') {
		if (textSearchInputClause) {
			textSearchInputClause.remove();
		} else {
			return ast.represents;
		}
	} else {
		const isIssueKey = isIssueKeyValue(val);
		const quotedKeyword = wrapWithQuotes(escapeBackslashes(val));

		const newTextSearchInputClause = createTextSearchInputClause({ quotedKeyword, isIssueKey });

		if (textSearchInputClause) {
			textSearchInputClause.replace(newTextSearchInputClause);
		} else {
			ast.query?.appendClause(newTextSearchInputClause, COMPOUND_OPERATOR_AND);
		}
	}

	return print(ast, {
		operatorCase: 'upper',
	});
};

const TextFieldType = {
	validate,
	value,
	update,
};

export default TextFieldType;
